// Pro mode "Ad" dock (M14): the selected text's full controls (its own text or which field it shows, font,
// weight, size, colour, alignment, caps, tracking, leading, box), then the ad form.
import { useT } from '../../../i18n';
import { useDesign } from '../../state';
import { useDesignUi } from '../designUi';
import { updateNode } from '../../commands/commands';
import { findNode } from '../../model/tree';
import { FONT_FAMILIES, familyOf } from '../../text/fontCatalog';
import { rgbHex, hexRgb } from '../../ad/brand';
import { resolveColor } from '../../render/paint';
import { isUncut, layoutText } from '../../text/layout';
import { useAdUi } from '../../ad/actions';
import type { TextNode, TextStyle } from '../../model/types';
import { AdForm } from './AdForm';
import s from './Ad.module.css';

const end = () => useDesign.getState().endGesture();

function TextProps({ n }: { n: TextNode }) {
  const t = useT();
  const doc = useDesign((d) => d.doc)!;
  const st = n.style;
  const upd = (fn: (x: TextNode) => void, merge?: string) =>
    useDesign.getState().run(updateNode(n.id, (x) => x.kind === 'text' && fn(x), 'cmdEdit', merge));
  const style = (p: Partial<TextStyle>, merge?: string) =>
    upd((x) => void (x.style = { ...x.style, ...p }), merge);
  const lay = layoutText(n);
  const fam = familyOf(st.family);
  return (
    <div data-testid="text-props">
      <h4>{t('adTextObject')}</h4>
      {n.bind && n.bind.role !== 'free' ? (
        <p className={s.small}>{t('adTextBound', { field: n.bind.role, lang: n.bind.lang ?? '' })}</p>
      ) : (
        <label className={s.field}>
          <span>{t('adTextContent')}</span>
          <textarea
            rows={2}
            data-testid="text-content"
            value={n.text}
            onChange={(e) => upd((x) => void (x.text = e.target.value), `txt-${n.id}`)}
            onBlur={end}
          />
        </label>
      )}
      <div className={s.pair}>
        <label className={s.field}>
          <span>{t('adFont')}</span>
          <select
            data-testid="text-family"
            value={st.family}
            onChange={(e) => (style({ family: e.target.value, fontRole: undefined, own: undefined }), end())}
          >
            {FONT_FAMILIES.map((f) => (
              <option key={f.family}>{f.family}</option>
            ))}
          </select>
        </label>
        <label className={s.field}>
          <span>{t('adWeight')}</span>
          <select value={st.weight} onChange={(e) => (style({ weight: Number(e.target.value) }), end())}>
            {(fam?.weights ?? [400]).map((w) => (
              <option key={w}>{w}</option>
            ))}
          </select>
        </label>
      </div>
      <div className={s.pair}>
        <label className={s.field}>
          <span>{t('adSize')}</span>
          <input
            type="number"
            min={4}
            max={600}
            data-testid="text-size"
            value={Math.round(st.size * 10) / 10}
            onChange={(e) => style({ size: Math.max(1, Number(e.target.value) || st.size) }, `sz-${n.id}`)}
            onBlur={end}
          />
        </label>
        <label className={s.field}>
          <span>{t('adColor')}</span>
          <input
            type="color"
            value={rgbHex(resolveColor(st.color, doc).rgb)}
            onChange={(e) => style({ color: { rgb: hexRgb(e.target.value), alpha: 1 } }, `col-${n.id}`)}
            onBlur={end}
          />
        </label>
      </div>
      <div className={s.pair}>
        <label className={s.field}>
          <span>{t('adAlign')}</span>
          <select
            value={st.align}
            onChange={(e) => (style({ align: e.target.value as TextStyle['align'] }), end())}
          >
            <option value="left">{t('alignLeftShort')}</option>
            <option value="center">{t('alignCenterShort')}</option>
            <option value="right">{t('alignRightShort')}</option>
          </select>
        </label>
        <label className={s.field}>
          <span>{t('adLines')}</span>
          <input
            type="number"
            min={1}
            max={12}
            value={n.box.maxLines}
            onChange={(e) =>
              upd(
                (x) => void (x.box = { ...x.box, maxLines: Math.max(1, Number(e.target.value) || 1) }),
                `ml-${n.id}`,
              )
            }
            onBlur={end}
          />
        </label>
      </div>
      <div className={s.pair}>
        <label className={s.field}>
          <span>{t('adTracking')}</span>
          <input
            type="number"
            step={10}
            value={st.tracking}
            onChange={(e) => style({ tracking: Number(e.target.value) || 0 }, `tr-${n.id}`)}
            onBlur={end}
          />
        </label>
        <label className={s.field}>
          <span>{t('adLeading')}</span>
          <input
            type="number"
            step={0.05}
            min={0.6}
            max={3}
            value={st.leading}
            onChange={(e) => style({ leading: Number(e.target.value) || 1.1 }, `ld-${n.id}`)}
            onBlur={end}
          />
        </label>
      </div>
      <label>
        <input
          type="checkbox"
          checked={st.caps}
          onChange={(e) => (style({ caps: e.target.checked }), end())}
        />{' '}
        {t('adCaps')}
      </label>
      {lay && lay.size < st.size - 0.05 && (
        <p className={s.small}>{t('adShrunk', { size: Math.round(lay.size) })}</p>
      )}
      {lay?.fellBack && (
        <p className={s.small} data-testid="text-brand-font-fallback">
          {t('adBrandFontFallback', { family: lay.family })}
        </p>
      )}
      {lay?.overflow && (
        <p className={s.small} style={{ color: 'var(--warn)' }}>
          {t(isUncut(n) ? 'adPriceTooWide' : 'adOverflow')}
        </p>
      )}
    </div>
  );
}

export function AdDock() {
  const t = useT();
  const doc = useDesign((d) => d.doc);
  const selection = useDesignUi((u) => u.selection);
  const sel = doc && selection.length === 1 ? findNode(doc, selection[0]!)?.node : null;
  return (
    <div className={s.form} style={{ border: 'none', padding: 0, background: 'none' }}>
      <button onClick={() => useAdUi.setState({ picker: true })} data-testid="dock-templates">
        {t('adTemplates')}
      </button>{' '}
      <button onClick={() => useAdUi.setState({ brandOpen: true })}>{t('adMyBrand')}</button>
      {sel?.kind === 'text' && <TextProps n={sel} />}
      <AdForm />
    </div>
  );
}
